/* eslint-disable react/prop-types */
/**
 * MOTRapidCaptureCard
 * ===================
 * Phase-1 MOT Rapid Capture screen. Renders ONE MCC entry at a time
 * using the category-specific schema fetched from
 * ``GET /api/mcc/rapid-capture/schemas``.
 *
 * Architectural rules:
 *   • Lightweight. Pricing-critical fields only. Tester is never asked
 *     to perform a second inspection.
 *   • Smart defaults pre-fill the obvious cases — tester confirms or
 *     overrides.
 *   • Pricing confidence + repair route are surfaced as 1-tap pill
 *     groups so FOH sees them as badges on the pricing screen.
 *   • Category fallback picker shown when the auto-mapper couldn't
 *     classify the DVSA wording — manual category selection seeds
 *     smart defaults via ``POST /mcc/.../remap``.
 *   • Legacy entries (no ``rapid_capture`` block, e.g. brake_disc with
 *     observed/suspected/confirmed) fall back to the parent caller —
 *     this component is rapid-capture-only.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Camera,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  Loader2,
  Gauge,
  Wrench,
  Droplet,
  Lightbulb,
  Volume2,
  Battery,
  Cable,
  Type,
  Square,
  Search,
  ChevronDown,
  ChevronUp,
  Send,
} from "lucide-react";
import PhotoAnnotator from "@/components/shared/PhotoAnnotator";

const LEVEL_STYLES = {
  4: { ring: "ring-red-500",   pill: "bg-red-600 text-white",       label: "REQUIRED" },
  3: { ring: "ring-amber-500", pill: "bg-amber-500 text-white",     label: "RECOMMENDED" },
  2: { ring: "ring-slate-400", pill: "bg-slate-200 text-slate-700", label: "OPTIONAL" },
};

const CONFIDENCE_STYLES = {
  high:   "bg-emerald-100 text-emerald-800 border-emerald-300",
  medium: "bg-amber-100   text-amber-800   border-amber-300",
  low:    "bg-red-100     text-red-800     border-red-300",
};

// Icon registry keyed by the ``icon`` string the BE returns in
// ``category_metadata``. Kept tight so the FE picker can render any
// future category without code changes — add a new icon here once and
// any new BE category referencing it just works.
const ICON_REGISTRY = {
  Gauge, Wrench, Droplet, Lightbulb, Volume2, Battery,
  Cable, Type, Square, AlertTriangle,
};

// Fallback flat list — used when the BE doesn't ship metadata yet
// (older deploys / silent-fail). New deploys always render the grouped
// picker via ``GroupedCategoryPicker`` below.
const FALLBACK_CATEGORY_OPTIONS = [
  { value: "tyre",            label: "Tyre",             icon: Gauge },
  { value: "brake",           label: "Brake",            icon: Wrench },
  { value: "suspension_arm",  label: "Suspension arm",   icon: Wrench },
  { value: "ball_joint",      label: "Ball joint",       icon: Wrench },
  { value: "shock_absorber",  label: "Shock absorber",   icon: Wrench },
  { value: "fluid_leak",      label: "Oil / fluid leak", icon: Droplet },
];

function PillRow({ options, value, onChange, multi, testidPrefix, required }) {
  const safe = Array.isArray(value) ? value : value ? [value] : [];
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const optVal = typeof opt === "string" ? opt : opt.value;
        const optLabel = typeof opt === "string" ? opt : opt.label;
        const on = safe.includes(optVal);
        return (
          <button
            key={optVal}
            type="button"
            onClick={() => {
              if (multi) {
                onChange(on ? safe.filter((v) => v !== optVal) : [...safe, optVal]);
              } else {
                // iter212b — For REQUIRED single-pill fields (e.g. wiper
                // blade_type) a re-tap on the already-selected pill
                // used to clear the value to null. That's the root of
                // the "wiper blade type won't save" report — a mobile
                // mis-tap silently wiped the required selection right
                // before Save & Next. Now a re-tap on the same pill is
                // a no-op; the tester must pick a DIFFERENT pill to
                // change their answer, or clear via a dedicated action.
                if (on) {
                  if (required) return; // keep the current value
                  onChange(null);
                } else {
                  onChange(optVal);
                }
              }
            }}
            className={`px-5 py-3 min-h-[52px] rounded-full text-base font-semibold border-2 transition-all active:scale-95 ${
              on
                ? "bg-teal-500 text-slate-900 border-teal-500 shadow-sm"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50 hover:border-slate-400"
            }`}
            data-testid={`${testidPrefix}-${optVal.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            {optLabel}
          </button>
        );
      })}
    </div>
  );
}

function ToggleRow({ on, onToggle, label, testid }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(!on)}
      className={`flex items-center justify-between w-full px-4 py-3.5 min-h-[56px] rounded-lg border-2 text-base font-semibold transition-all active:scale-[0.99] ${
        on
          ? "bg-teal-50 border-teal-500 text-teal-900"
          : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400"
      }`}
      data-testid={testid}
    >
      <span>{label}</span>
      <span
        className={`w-14 h-8 rounded-full relative transition-colors flex-shrink-0 ${
          on ? "bg-teal-500" : "bg-slate-300"
        }`}
      >
        <span
          className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-all ${
            on ? "left-7" : "left-1"
          }`}
        />
      </span>
    </button>
  );
}

function PhotoStrip({ photos, onAdd, busy }) {
  const safe = photos || [];
  const fileRef = useRef(null);
  // iter198nn — Hold the raw camera image while the tester annotates.
  const [pendingDataUrl, setPendingDataUrl] = useState(null);
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setPendingDataUrl(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-1">
      {safe.map((p, i) => (
        <img
          key={i}
          src={p.url}
          alt={p.label || `evidence-${i}`}
          className="w-28 h-28 rounded-lg object-cover border-2 border-slate-300 flex-shrink-0"
        />
      ))}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFile}
        className="hidden"
        data-testid="rapid-photo-input"
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        className="w-28 h-28 rounded-lg bg-teal-50 border-2 border-dashed border-teal-500 flex flex-col items-center justify-center text-teal-700 hover:bg-teal-100 hover:border-teal-600 active:scale-95 disabled:opacity-50 transition-all flex-shrink-0 shadow-sm"
        data-testid="rapid-photo-add"
      >
        {busy ? <Loader2 className="w-10 h-10 animate-spin" /> : <Camera className="w-10 h-10" />}
        <span className="text-sm mt-1.5 font-bold tracking-wide">PHOTO</span>
      </button>
      {pendingDataUrl && (
        <PhotoAnnotator
          sourceDataUrl={pendingDataUrl}
          onSave={(annotatedUrl) => {
            setPendingDataUrl(null);
            onAdd(annotatedUrl);
          }}
          onCancel={() => setPendingDataUrl(null)}
        />
      )}
    </div>
  );
}

function CategoryPicker({ onPick, busy }) {
  return (
    <div className="rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 p-4 mb-4">
      <p className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-2">
        Pick a category for this defect
      </p>
      <p className="text-sm text-slate-600 mb-3 leading-snug">
        The MOT wording didn't match a known repair category. Pick the
        closest one — we'll pre-fill with sensible defaults that you can
        confirm or override.
      </p>
      <div className="grid grid-cols-2 gap-2">
        {FALLBACK_CATEGORY_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          return (
            <button
              key={opt.value}
              type="button"
              disabled={busy}
              onClick={() => onPick(opt.value)}
              className="flex items-center gap-2 px-4 py-3 min-h-[52px] rounded-lg border-2 border-slate-300 bg-white hover:bg-teal-50 hover:border-teal-400 disabled:opacity-50 text-base font-semibold text-slate-800"
              data-testid={`rapid-pick-${opt.value}`}
            >
              <Icon className="w-5 h-5" />
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * GroupedCategoryPicker
 * ---------------------
 * Phase-4 category picker. Replaces the flat 6-button grid with a
 * grouped, frequency-sorted, searchable list of all ~40 rapid-capture
 * categories the BE supports. Groups are vehicle-area buckets (Brakes,
 * Lights & Electrical, Suspension & Steering, …) returned from the
 * ``GET /api/mcc/rapid-capture/schemas`` endpoint as ``category_groups``.
 *
 * UX rules:
 *   • Search box at the top filters by label + keyword (``search_terms``)
 *     across all groups. When active, group headers collapse into a
 *     flat result list so the tester sees matches instantly.
 *   • Each group renders as a collapsible section, ordered by overall
 *     frequency (``group.rank``). The first 2 groups (Tyres & Wheels
 *     and Brakes) start expanded — these cover the bulk of MOT picks.
 *   • Within a group, categories are ordered by within-group
 *     ``frequency_rank`` (lower = more common).
 *   • Icons resolved from ``ICON_REGISTRY`` keyed on the BE-supplied
 *     ``icon`` string. Falls back to Wrench when unknown.
 */
function GroupedCategoryPicker({
  categoryMetadata,
  categoryGroups,
  schemas,
  onPick,
  busy,
}) {
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState(() => {
    // Collapse all groups EXCEPT the first 2 (highest-frequency).
    const init = {};
    (categoryGroups || []).forEach((g, i) => { init[g.key] = i >= 2; });
    return init;
  });

  // Build group → [categories] mapping, ordered by frequency_rank.
  const grouped = useMemo(() => {
    const out = {};
    (categoryGroups || []).forEach((g) => { out[g.key] = []; });
    Object.entries(categoryMetadata || {}).forEach(([cat, meta]) => {
      if (!schemas?.[cat]) return; // skip cats without schema (defensive)
      const gk = meta.group;
      if (!out[gk]) out[gk] = [];
      out[gk].push({ cat, meta });
    });
    Object.values(out).forEach((arr) => {
      arr.sort((a, b) =>
        (a.meta.frequency_rank || 99) - (b.meta.frequency_rank || 99),
      );
    });
    return out;
  }, [categoryMetadata, categoryGroups, schemas]);

  // Flatten + filter for search mode.
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    const hits = [];
    Object.entries(categoryMetadata || {}).forEach(([cat, meta]) => {
      if (!schemas?.[cat]) return;
      const hay = [
        cat,
        meta.label || "",
        ...(meta.search_terms || []),
      ].join(" ").toLowerCase();
      if (hay.includes(q)) hits.push({ cat, meta });
    });
    hits.sort((a, b) =>
      (a.meta.frequency_rank || 99) - (b.meta.frequency_rank || 99),
    );
    return hits;
  }, [search, categoryMetadata, schemas]);

  const renderButton = ({ cat, meta }) => {
    const Icon = ICON_REGISTRY[meta.icon] || Wrench;
    return (
      <button
        key={cat}
        type="button"
        disabled={busy}
        onClick={() => onPick(cat)}
        className="flex items-center gap-2 px-3 py-3 min-h-[52px] rounded-lg border-2 border-slate-300 bg-white hover:bg-teal-50 hover:border-teal-400 disabled:opacity-50 text-sm font-semibold text-slate-800 text-left"
        data-testid={`rapid-pick-${cat}`}
      >
        <Icon className="w-5 h-5 flex-shrink-0 text-slate-500" />
        <span className="leading-tight">{meta.label || cat}</span>
      </button>
    );
  };

  return (
    <div
      className="rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 p-4 mb-4"
      data-testid="rapid-grouped-picker"
    >
      <p className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-2">
        Pick a category for this defect
      </p>
      <p className="text-sm text-slate-600 mb-3 leading-snug">
        Search or browse by vehicle area — we'll pre-fill sensible
        defaults you can confirm or override.
      </p>

      {/* Search bar */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search categories (e.g. brake pipe, headlamp, sill)"
          className="w-full pl-10 pr-9 py-2.5 text-base border-2 border-slate-300 rounded-lg bg-white focus:outline-none focus:border-teal-500"
          data-testid="rapid-picker-search"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1"
            data-testid="rapid-picker-search-clear"
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Search-mode flat result list */}
      {searchResults && (
        <div data-testid="rapid-picker-search-results">
          {searchResults.length === 0 ? (
            <p className="text-sm text-slate-500 italic py-3 text-center">
              No matches — try a different keyword or pick from a group.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {searchResults.map(renderButton)}
            </div>
          )}
        </div>
      )}

      {/* Browse-mode grouped collapsible sections */}
      {!searchResults && (
        <div className="space-y-2">
          {(categoryGroups || []).map((g) => {
            const items = grouped[g.key] || [];
            if (items.length === 0) return null;
            const isCollapsed = collapsed[g.key];
            return (
              <div
                key={g.key}
                className="bg-white rounded-lg border border-slate-200 overflow-hidden"
                data-testid={`rapid-picker-group-${g.key}`}
              >
                <button
                  type="button"
                  onClick={() =>
                    setCollapsed((c) => ({ ...c, [g.key]: !c[g.key] }))
                  }
                  className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-slate-50"
                  data-testid={`rapid-picker-group-toggle-${g.key}`}
                  aria-expanded={!isCollapsed}
                >
                  <span className="text-sm font-bold text-slate-800 uppercase tracking-wide">
                    {g.label}
                    <span className="ml-2 text-xs font-semibold text-slate-500">
                      {items.length}
                    </span>
                  </span>
                  {isCollapsed ? (
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  ) : (
                    <ChevronUp className="w-4 h-4 text-slate-500" />
                  )}
                </button>
                {!isCollapsed && (
                  <div className="grid grid-cols-2 gap-2 p-2 pt-1 border-t border-slate-100">
                    {items.map(renderButton)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function MOTRapidCaptureCard({
  outcomeId,
  entries,
  schemas,
  pricingConfidenceOptions,
  repairRouteOptions,
  categoryMetadata,
  categoryGroups,
  onClose,
  onCompleteAll,
  onEntriesRefresh,
  onPropagateTyreSpec,
}) {
  // Sort: pending first (LEVEL 4 highest), then done.
  const ordered = useMemo(() => {
    const open = entries.filter((e) => e.status === "pending");
    const done = entries.filter((e) => e.status !== "pending");
    open.sort((a, b) => (b.level || 0) - (a.level || 0));
    return [...open, ...done];
  }, [entries]);

  const [idx, setIdx] = useState(0);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [err, setErr] = useState("");

  // iter197y — Scroll-reset sentinel. When the active entry changes
  // (Save & Next, Skip, Escalate, prev arrow), the modal must scroll
  // back to the top of the new card so the tester doesn't have to
  // scroll up by hand. We attach a ref to a hidden anchor at the top
  // of the card body and call `scrollIntoView` on every entry change.
  const topAnchorRef = useRef(null);

  const current = ordered[idx];
  const rc = current?.rapid_capture || null;
  const schema = rc?.category ? schemas[rc.category] : null;

  // Seed the draft whenever the active entry changes.
  useEffect(() => {
    if (!current) return;
    setDraft({
      fields:             rc?.fields || {},
      pricing_confidence: rc?.pricing_confidence || null,
      repair_route:       rc?.repair_route || null,
      // iter197h — Tester's estimated labour hours (guidance for FOH).
      tester_estimated_hours: rc?.tester_estimated_hours ?? null,
      photos:             current.photos || [],
    });
    setErr("");
    // iter197y — Reset scroll to the top of the modal whenever the
    // active entry changes. Without this, the modal stays scrolled at
    // the previous card's depth, which made Save & Next feel broken
    // because the tester had to scroll back up to read the next
    // defect description. The anchor sits at the very top of the
    // card body so `scrollIntoView` lifts the heading + defect text
    // into view first.
    if (topAnchorRef.current) {
      // requestAnimationFrame guarantees the DOM has re-rendered with
      // the new entry's content before we measure / scroll. Using
      // "auto" instead of "smooth" because testers prefer an instant
      // reset over a half-second animation between cards.
      requestAnimationFrame(() => {
        try {
          topAnchorRef.current?.scrollIntoView({
            behavior: "auto",
            block:    "start",
          });
        } catch { /* IE / older Safari — non-fatal */ }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, rc?.category]);

  if (!current) {
    return (
      <Card className="p-8 text-center" data-testid="rapid-empty">
        <Check className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
        <p className="font-bold text-xl mb-4">All MCC items captured.</p>
        <Button className="min-h-[52px] text-base px-6" onClick={onClose} data-testid="rapid-close-empty">
          Back to worksheet
        </Button>
      </Card>
    );
  }

  const sty = LEVEL_STYLES[current.level] || LEVEL_STYLES[2];

  const persistPatch = async (patch, opts = {}) => {
    setSaving(true);
    setErr("");
    try {
      const r = await axios.put(
        `${API}/mcc/outcomes/${outcomeId}/entries/${current.id}`,
        { ...patch, auto_complete: !!opts.autoComplete },
      );
      return r.data?.entry;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handlePickCategory = async (category) => {
    setSaving(true);
    setErr("");
    try {
      const r = await axios.post(
        `${API}/mcc/outcomes/${outcomeId}/entries/${current.id}/remap`,
        { category },
      );
      const updated = r.data?.entry;
      if (updated) {
        const newRc = updated.rapid_capture || {};
        setDraft({
          fields:             newRc.fields || {},
          pricing_confidence: newRc.pricing_confidence || null,
          repair_route:       newRc.repair_route || null,
          photos:             updated.photos || [],
        });
        // iter197y — Was previously calling onCompleteAll, which the
        // parent wires to setMccOpen(false). That meant picking a
        // category mid-flow would KICK THE TESTER OUT of the modal.
        // Use the new entries-only refresh hook so the parent updates
        // its mccEntries state in place and the modal stays open on
        // the same card.
        await onEntriesRefresh?.();
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Category pick failed");
    } finally {
      setSaving(false);
    }
  };

  const handleField = (key, value) => {
    setDraft((d) => ({
      ...d,
      fields: { ...(d.fields || {}), [key]: value },
    }));
  };

  // iter211al — Tyre size/brand auto-transfer fires on BLUR (not on
  // every keystroke). Prior behaviour propagated partial mid-typing
  // values ("2" of "205/55 R16 91V") to siblings, and the backend's
  // "never overwrite" rule then locked siblings permanently at the
  // partial value (GN64YZE bug). Blur guarantees the tester has
  // finished typing before any sibling gets touched.
  const handleTyreSpecBlur = (key, value) => {
    if (rc?.category !== "tyre") return;
    if (key !== "size" && key !== "brand") return;
    const v = String(value || "").trim();
    if (!v || !current?.id) return;
    onPropagateTyreSpec?.(key, v, current.id);
  };

  const handleSaveAndNext = async () => {
    if (!rc?.category) {
      setErr("Pick a category first.");
      return;
    }
    const saved = await persistPatch(
      {
        rapid_capture: {
          fields:                 draft.fields,
          pricing_confidence:     draft.pricing_confidence,
          repair_route:           draft.repair_route,
          // iter197h — Persist the tester's labour estimate so FOH
          // pricing UI can surface it on the MCC info pill.
          tester_estimated_hours: draft.tester_estimated_hours,
        },
      },
      { autoComplete: true },
    );
    if (!saved) return;
    if (idx < ordered.length - 1) setIdx(idx + 1);
    else onCompleteAll?.();
  };

  const handleSkip = async () => {
    if (current.level >= 4) {
      setErr("LEVEL 4 cards cannot be skipped — capture minimum evidence or escalate.");
      return;
    }
    // iter197c — Block Skip when the entry hasn't been classified yet.
    // Without a category, the entry has zero pricing context — skipping
    // here previously made the card vanish from the workspace with no
    // recovery UI (WX12FYD bug). Force the tester to pick a category
    // (or close the modal) instead.
    if (!rc?.category) {
      setErr(
        "Pick a category first — skipping an unclassified card removes it " +
        "from the workspace. Tap the closest match (you can refine fields after)."
      );
      return;
    }
    const saved = await persistPatch({ status: "skipped" });
    if (!saved) return;
    if (idx < ordered.length - 1) setIdx(idx + 1);
    else onCompleteAll?.();
  };

  const handlePhotoAdd = async (dataUrl) => {
    setPhotoBusy(true);
    try {
      const r = await axios.post(
        `${API}/mcc/outcomes/${outcomeId}/entries/${current.id}/photo`,
        { url: dataUrl, label: "" },
      );
      const newPhotos = r.data?.entry?.photos || [];
      setDraft((d) => ({ ...d, photos: newPhotos }));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Photo upload failed");
    } finally {
      setPhotoBusy(false);
    }
  };

  const renderField = (field) => {
    const value = draft.fields?.[field.key];
    if (field.type === "single_pill") {
      return (
        <div className="mb-4" key={field.key}>
          <p className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-2">
            {field.label}
            {field.required && (
              <span className="ml-1 text-red-500" aria-label="required">*</span>
            )}
          </p>
          <PillRow
            options={field.options}
            value={value || null}
            onChange={(v) => handleField(field.key, v)}
            required={!!field.required}
            testidPrefix={`rapid-${field.key}`}
          />
        </div>
      );
    }
    if (field.type === "multi_pill") {
      return (
        <div className="mb-4" key={field.key}>
          <p className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-2">
            {field.label}
          </p>
          <PillRow
            options={field.options}
            value={value || []}
            onChange={(v) => handleField(field.key, v)}
            multi
            testidPrefix={`rapid-${field.key}`}
          />
        </div>
      );
    }
    if (field.type === "toggle") {
      return (
        <div className="mb-3" key={field.key}>
          <ToggleRow
            on={!!value}
            onToggle={(v) => handleField(field.key, v)}
            label={field.label}
            testid={`rapid-toggle-${field.key}`}
          />
        </div>
      );
    }
    if (field.type === "textarea") {
      // iter211ad — Free-text notes field (multi-line) for tester
      // outlying-case commentary. Rendered as a full-width block so
      // long entries breathe.
      return (
        <div className="mb-4" key={field.key}>
          <p className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-2">
            {field.label}
          </p>
          <textarea
            value={value ?? ""}
            onChange={(e) => handleField(field.key, e.target.value)}
            placeholder={field.placeholder || ""}
            rows={3}
            className="w-full px-3 py-3 text-base border-2 border-slate-300 rounded-lg bg-white focus:outline-none focus:border-teal-500 resize-y"
            data-testid={`rapid-textarea-${field.key}`}
          />
        </div>
      );
    }
    if (field.type === "text") {
      // size from another tyre and the tester hasn't changed it, surface
      // a subtle "auto-filled" chip so they know to override for a
      // staggered fit. Clears the moment they edit the field.
      const showAutofilledHint =
        rc?.category === "tyre"
        && field.key === "size"
        && !!current?._autofilled_size
        && (value || "") === current._autofilled_size;
      return (
        <div className="mb-4" key={field.key}>
          <p className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-2">
            {field.label}
          </p>
          <input
            type="text"
            value={value ?? ""}
            onChange={(e) => handleField(field.key, e.target.value)}
            onBlur={(e) => handleTyreSpecBlur(field.key, e.target.value)}
            placeholder={field.placeholder || ""}
            className="w-full px-3 py-3 text-base border-2 border-slate-300 rounded-lg bg-white focus:outline-none focus:border-teal-500"
            data-testid={`rapid-text-${field.key}`}
          />
          {showAutofilledHint && (
            <p
              className="text-xs text-teal-700 mt-1.5 font-medium flex items-center gap-1"
              data-testid={`rapid-tyre-${field.key}-autofilled-hint`}
            >
              <Check className="w-3.5 h-3.5" />
              Auto-filled from another tyre · tap to override for staggered fit / mixed brand
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Legacy entries (no rapid_capture seed AND no schema match) — render
  // a category picker so the tester can promote it.
  const showPicker = !rc?.category;

  // Severity-aware left rail. Visible from across the workshop —
  // tester sees priority before reading text. Maps to the same
  // colour palette the FOH dashboard already uses, so the visual
  // language is consistent across the app.
  const RAIL_STYLES = {
    4: "before:bg-red-600",     // LEVEL 4 — Dangerous / blocking
    3: "before:bg-amber-500",   // LEVEL 3 — Major / recommended
    2: "before:bg-slate-400",   // LEVEL 2 — Advisory / optional
  };
  const railClass = RAIL_STYLES[current.level] || RAIL_STYLES[2];

  // "Ready to save" — derived from category completeness gating on
  // the client side so the tester gets instant feedback. Mirrors the
  // backend `is_rapid_capture_complete` gate without making a round
  // trip. Used to colour-cue the sticky save bar.
  const readyToSave = (() => {
    if (showPicker || !schema) return false;
    // pricing_confidence and at least one required field need values.
    if (!draft.pricing_confidence) return false;
    for (const f of schema.fields) {
      if (!f.required) continue;
      const v = draft.fields?.[f.key];
      if (v === undefined || v === null || v === "" ||
          (Array.isArray(v) && v.length === 0)) {
        return false;
      }
    }
    return true;
  })();

  return (
    <Card
      className={`relative overflow-hidden p-0 ring-2 ${sty.ring} ` +
        `before:absolute before:left-0 before:top-0 before:bottom-0 ` +
        `before:w-1.5 ${railClass}`}
      data-testid={`rapid-card-${current.id}`}
    >
      {/* Scrollable content area — the sticky action bar lives BELOW
          this so the tester can always reach Save & Next without
          scrolling. Max-height keeps long forms from pushing the bar
          off-screen on small viewports. */}
      <div className="p-4 pl-5 pb-3 max-h-[70vh] overflow-y-auto">
        {/* iter197y — Top-of-card scroll anchor. The seed useEffect
            calls scrollIntoView() on this whenever the active entry
            changes so the tester always lands at the top of the new
            card after Save & Next / Skip / Escalate / prev. */}
        <div ref={topAnchorRef} aria-hidden="true" />
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-3 py-1.5 rounded-full text-sm font-bold ${sty.pill}`}>
              {sty.label}
            </span>
            <span className="text-base text-slate-600 font-semibold uppercase tracking-wide">
              {schema?.label || "Rapid capture"} · {idx + 1} / {ordered.length}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-2"
            data-testid="rapid-close-btn"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <p className="text-lg leading-snug font-semibold text-slate-900 mb-3" data-testid="rapid-defect-text">
          {current.defect_text}
        </p>

        {/* iter198qq — FOH bounce-back reason. When FOH returns this
            MCC card to the tester from pricing with a reason (e.g.
            "Need wear pattern + measurement on OSF disc"), surface
            it INSIDE the rapid-capture card so the tester can read
            it while filling in the answer. Previously the reason
            only appeared in a workspace-page banner that the
            auto-opened MCC modal covered — testers literally never
            saw it. */}
        {(current.bounce_back_reason || "").trim() && (
          <div
            className="mb-5 rounded-lg border-2 border-purple-400 bg-purple-50 px-4 py-3 flex items-start gap-3"
            data-testid={`rapid-foh-return-reason-${current.id}`}
          >
            <div className="rounded-full bg-purple-100 p-1.5 shrink-0">
              <Send className="w-4 h-4 text-purple-700" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold text-purple-900 uppercase tracking-wide">
                FOH returned this — please action
              </p>
              <p className="text-sm text-purple-900 mt-1 leading-snug italic">
                "{current.bounce_back_reason}"
              </p>
            </div>
          </div>
        )}

        {showPicker && (
          (categoryMetadata && Object.keys(categoryMetadata).length > 0) ? (
            <GroupedCategoryPicker
              categoryMetadata={categoryMetadata}
              categoryGroups={categoryGroups || []}
              schemas={schemas}
              onPick={handlePickCategory}
              busy={saving}
            />
          ) : (
            <CategoryPicker onPick={handlePickCategory} busy={saving} />
          )
        )}

        {!showPicker && schema && (
          <>
            {schema.fields.map(renderField)}

            {/* Pricing confidence — bigger tap targets for greasy gloves. */}
            <div className="mb-4 pt-4 border-t border-slate-200">
              <p className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-2">
                Pricing confidence
              </p>
              <div className="flex flex-wrap gap-2">
                {(pricingConfidenceOptions || []).map((opt) => {
                  const on = draft.pricing_confidence === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() =>
                        setDraft((d) => ({
                          ...d,
                          pricing_confidence: on ? null : opt.value,
                        }))
                      }
                      className={`px-5 py-3 min-h-[52px] rounded-full text-base font-semibold border-2 transition-all active:scale-95 ${
                        on
                          ? CONFIDENCE_STYLES[opt.value] ||
                            "bg-teal-500 text-white border-teal-500"
                          : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50 hover:border-slate-400"
                      }`}
                      data-testid={`rapid-confidence-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Repair route — 1-tap pills (already use the bigger PillRow). */}
            <div className="mb-4">
              <p className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-2">
                Repair route
              </p>
              <PillRow
                options={(repairRouteOptions || []).map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
                value={draft.repair_route}
                onChange={(v) =>
                  setDraft((d) => ({ ...d, repair_route: v }))
                }
                testidPrefix="rapid-route"
              />
            </div>

            {/* iter197h — Tester's labour estimate for this job. Free
                numeric input (hours, in 0.25 increments). Used purely
                as guidance — appears on the FOH pricing screen MCC
                info pill so FOH knows roughly how long the tester
                thinks the job will take. FOH still sets the actual
                labour line. */}
            <div className="mb-4">
              <p className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-2">
                Estimated labour time
                <span className="ml-1.5 font-normal text-[11px] text-slate-500 tracking-normal">
                  (guidance for FOH pricing)
                </span>
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  max="40"
                  placeholder="e.g. 1.0"
                  value={draft.tester_estimated_hours ?? ""}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") {
                      setDraft((d) => ({ ...d, tester_estimated_hours: null }));
                    } else {
                      const n = Number(raw);
                      setDraft((d) => ({
                        ...d,
                        tester_estimated_hours: Number.isFinite(n) ? n : null,
                      }));
                    }
                  }}
                  className="w-32 px-4 py-3 min-h-[52px] text-base font-semibold border-2 border-slate-300 rounded-lg focus:border-teal-500 focus:outline-none"
                  data-testid="rapid-tester-hours-input"
                />
                <span className="text-base text-slate-600">hours</span>
              </div>
            </div>

            {/* Photos */}
            <div className="mb-4">
              <p className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-2">
                Photos {schema.photo_hints?.length ? `· ${schema.photo_hints.join(" / ")}` : ""}
              </p>
              <PhotoStrip
                photos={draft.photos}
                onAdd={handlePhotoAdd}
                busy={photoBusy}
              />
            </div>
          </>
        )}

        {err && (
          <div className="mb-2 text-sm text-red-700 font-semibold flex items-center gap-1.5" data-testid="rapid-error">
            <AlertTriangle className="w-4 h-4" /> {err}
          </div>
        )}

        {/* iter197c — "Re-open" banner when a previously-skipped or
            completed MCC card is being viewed. Gives the tester a clear
            recovery path (e.g. WX12FYD: skipped an unclassified card,
            now needs to capture it). One-tap flips status back to
            pending; the modal will surface it on the next reload. */}
        {current.status !== "pending" && (
          <div
            className="mb-2 mt-1 rounded-lg border-2 border-amber-300 bg-amber-50 px-4 py-3 flex items-center justify-between gap-3"
            data-testid="rapid-reopen-banner"
          >
            <div className="min-w-0">
              <p className="text-sm font-bold text-amber-900">
                This card is marked {current.status === "skipped" ? "Skipped" : "Complete"}.
              </p>
              <p className="text-xs text-amber-800 mt-0.5">
                Tap Re-open to capture {current.status === "skipped" ? "evidence" : "fresh evidence"} for this defect.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={async () => {
                const saved = await persistPatch({ status: "pending" });
                // iter197y — Re-opening a complete/skipped entry should
                // keep the tester on the SAME card so they can resume
                // capturing fresh evidence. Was previously calling
                // onCompleteAll, which closed the modal and forced the
                // tester to re-enter MCC from scratch.
                if (saved) await onEntriesRefresh?.();
              }}
              disabled={saving}
              className="border-amber-500 bg-white text-amber-900 hover:bg-amber-100 min-h-[44px] font-semibold shrink-0"
              data-testid="rapid-reopen-btn"
            >
              Re-open
            </Button>
          </div>
        )}
      </div>

      {/* Sticky action bar — always visible at the bottom of the modal
          so Save & Next can be reached without scrolling. Background
          colour cues whether the form has the minimum data for save
          (green hint when ready). */}
      <div
        className={`sticky bottom-0 z-10 flex items-center gap-2 px-4 py-3.5 border-t-2 ${
          readyToSave
            ? "bg-emerald-50 border-emerald-200"
            : "bg-white border-slate-200"
        }`}
      >
        <Button
          variant="outline"
          onClick={() => setIdx(Math.max(0, idx - 1))}
          disabled={idx === 0 || saving}
          className="min-h-[52px] px-4"
          data-testid="rapid-prev"
          aria-label="Previous"
        >
          <ChevronLeft className="w-6 h-6" />
        </Button>
        <Button
          variant="ghost"
          onClick={handleSkip}
          disabled={saving || current.level >= 4}
          className="text-slate-600 min-h-[52px] text-base font-semibold"
          data-testid="rapid-skip"
        >
          Skip
        </Button>
        <div className="flex-1 text-center">
          {readyToSave && !saving && (
            <span className="text-sm font-bold text-emerald-700 uppercase tracking-wide">
              Ready to save
            </span>
          )}
        </div>
        <Button
          onClick={handleSaveAndNext}
          disabled={saving || showPicker}
          className="bg-teal-600 hover:bg-teal-700 text-white min-h-[56px] px-6 text-base font-bold"
          data-testid="rapid-save-next"
        >
          {saving ? (
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <Check className="w-5 h-5 mr-2" />
          )}
          {idx < ordered.length - 1 ? "Save & Next" : "Save & Finish"}
          {idx < ordered.length - 1 && <ChevronRight className="w-5 h-5 ml-1" />}
        </Button>
      </div>
    </Card>
  );
}
